import React, { useEffect, useState, useCallback } from "react";
import Header from "~components/header";
import { apiErrorHandler } from "~utils/apiErrorHandler";
import { useSelector } from "react-redux";
import { Buttons } from "~components/buttons-v1";
import { useHistory } from "react-router";
import { useMutation, useLazyQuery } from "@apollo/react-hooks";
import { UPDATE_SUBSCRIPTION } from "../../graphQL/mutations";
import { GET_SUBSCRIPTION, GET_SUBSCRIPTION_MODULES } from "../../graphQL/queries";
import { Spinner } from "~components/spinner";
import _ from "lodash";
import { convertPricetoFormat } from "~utils/priceFormatter";
import "../../resources/less/forms.less";
import "./index.less";

import { DashboardArrowIcon, CheckIcon, InfoIcon } from "~utils/SVG";

import mudeyIcon from "~images/icons/favicon.ico";
import brandingIcon from "~images/icons/branding.svg";
import guroIcon from "~images/icons/guro.svg";
import mudeyAiIcon from "~images/icons/wand.svg";

export default function PricingDetails() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const nif = useSelector((state: any) => state?.userData?.nif);
  const partnerStatus = useSelector((state: any) => state?.userData?.verified);
  const isVerifiedAccount = useSelector((state: any) => state?.userData?.verified == "VERIFIED");
  const [subscriptionPlan, setSubscriptionPlan] = useState({
    base: true,
    branding: false,
    guroAI: true,
    mudeyAI: true
  });
  // Module code -> monthly price, from the subscription_modules catalogue. The displayed
  // total is summed from this and the selected modules, never from a stored running total:
  // reading a server value back as the base is what let a wrong number compound.
  const [modulePrices, setModulePrices] = useState({});

  /* ----------------------------------- API ---------------------------------- */
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);

  const [getSubscriptionModules] = useLazyQuery(GET_SUBSCRIPTION_MODULES, {
    fetchPolicy: "no-cache",
    onError: useCallback((error: any) => {
      apiErrorHandler(error);
      setIsLoading(false);
    }, []),
    onCompleted: useCallback(({ getSubscriptionModules }) => {
      setModulePrices(
        _.reduce(getSubscriptionModules, (prices, module) => ({
          ...prices,
          [module.code]: module.monthlyPrice
        }), {})
      );
    }, []),
  });

  const [getSubscription] = useLazyQuery(GET_SUBSCRIPTION, {
    fetchPolicy: "no-cache",
    onError: useCallback((error: any) => {
      apiErrorHandler(error);
      setIsLoading(false);
    }, []),
    onCompleted: useCallback(({ getPartnerCurrentSubscription }) => {
      const { subscriptionPlan } = getPartnerCurrentSubscription;

      // A partner who has not subscribed yet has no stored plan, and this page is where they
      // pick one. Deriving the selection from an absent plan leaves every module unticked,
      // which shows 0,00 and submits an empty subscriptionPlan on activation — so fall back
      // to the base plan and the modules included with it instead.
      if (_.isEmpty(subscriptionPlan)) {
        setSubscriptionPlan({
          base: true,
          branding: false,
          guroAI: true,
          mudeyAI: true
        });

        setIsLoading(false);

        return;
      }

      const base = _.includes(subscriptionPlan, 'BASE');
      const branding = _.includes(subscriptionPlan, 'BRANDING');
      const guroAI = _.includes(subscriptionPlan, 'GUROAI');
      const mudeyAI = _.includes(subscriptionPlan, 'MUDEYAI');

      setSubscriptionPlan({
        base,
        branding,
        guroAI,
        mudeyAI
      });

      setIsLoading(false);
    }, []),
  });

  /* ---------------------------------- HOOKS --------------------------------- */
  useEffect(() => {
    getSubscriptionModules();
  }, []);

  useEffect(() => {
    if (nif) {
      getSubscription({
        variables: {
          nif,
        },
      });
    }
  }, [nif]);

  /* -------------------------------- FUNCTIONS ------------------------------- */
  function getSelectedModules() {
    const selectedModules = [];

    if (subscriptionPlan.base) {
      selectedModules.push("BASE");
    }
    if (subscriptionPlan.branding) {
      selectedModules.push("BRANDING");
    }
    if (subscriptionPlan.guroAI) {
      selectedModules.push("GUROAI");
    }
    if (subscriptionPlan.mudeyAI) {
      selectedModules.push("MUDEYAI");
    }

    return selectedModules;
  }

  // The total is always summed from the catalogue over what is currently selected, so the
  // figure on screen matches the modules shown and cannot inherit an earlier wrong value.
  function getMonthlyValue() {
    return _.sum(_.map(getSelectedModules(), code => _.get(modulePrices, code, 0)));
  }

  function getModulePrice(code) {
    return _.get(modulePrices, code, 0);
  }

  // Free modules read "Gratuito" rather than 0,00 €, and stop doing so by themselves if the
  // catalogue ever gives them a price.
  function getModulePriceLabel(code) {
    const price = getModulePrice(code);

    return price > 0 ? `${convertPricetoFormat(price, false, "", 2, 2)} €/mês` : "Gratuito";
  }

  async function handleActivateSubscription() {
    const selectedModules = getSelectedModules();

    try {
      const { data } = await updateSubscription({
        variables: {
          nif,
          input: {
            subscriptionPlan: selectedModules,
          },
        },
      });

      if (data) {
        if (partnerStatus == "VERIFIED") {
          return history.push("/configuracoes");
        }

        return history.push("/formulário-payment");
      }
    } catch (error) {
      apiErrorHandler(error);
    }
  }

  function handleModuleClick(clickedModule) {
    // * Only this module can be altered at this time
    const selectedModule = {
      BRANDING: 'branding'
    }[clickedModule];

    if (!selectedModule) return;

    // Only the selection changes here: the price follows from it via getMonthlyValue.
    setSubscriptionPlan({
      ...subscriptionPlan,
      [selectedModule]: !subscriptionPlan[selectedModule]
    })
  }

  /* ----------------------------------- JSX ---------------------------------- */
  return (
    <section className="pricing-details">
      <Header
        title="Subscrição"
        paraTitle="Escolhe os módulos a que queres ter acesso. Podes adicionar ou retirar módulos adicionais a qualquer altura."
        backButtonText={!isVerifiedAccount ? "<- Voltar atrás" : null}
        backButtonClick={() =>
          !isVerifiedAccount ? history.push(`/formulario-adesao`) : null
        }
      />
      {isLoading && <Spinner />}

      <div className="pricing-form">
        <div className="ball"/>

        <label className="title">Configura a tua MudeyPRO</label>
        <label className="description">Escolhe os módulos que melhor servem a tua equipa.</label>

        <div className="modules">
          {/* Base Module */}
          <div className="base-module">
            <div className="top-section">
              <div className="left">
                <div className="info-holder">
                  <img src={mudeyIcon} width="40" height="40"/>
                  <div className="info">
                    <label className="title">Módulo Base</label>
                    <label className="description">Obrigatório • Gestão essencial</label>
                    <label className="extra">Isento se atingires 200€ de produção nova cobrada no mês</label>
                  </div>
                </div>
              </div>

              <div className="right">
                <label className="price">{convertPricetoFormat(getModulePrice("BASE"), false, "", 2, 2)} €/mês</label>
                <label>Acresce iva · sempre</label>
                <label>Incluído</label>
              </div>
            </div>

            <hr/>

            <div className="bottom-section">
              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Simuladores automáticos</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Gestão de leads</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Gestão de recibos</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Carteira de apólices</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Processo de renovações</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Módulo de sinistros</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Apoio personalizado da equipa MudeyPRO</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Formação contínua</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Acesso comunidade MudeyPRO</label>
              </div>

              <div className="list-item">
                <CheckIcon
                  fill="var(--primary-color)"
                  size="15"
                />
                <label>Acesso a +20 seguradoras</label>
              </div>
            </div>
          </div>

          {/* Branding Module */}
          <div onClick={() => handleModuleClick('BRANDING')} className="branding-module">
            <div style={{ backgroundColor: 'var(--secondary-color)' }} className="img-wrapper">
              <img src={brandingIcon} />
            </div>

            <div className="info">
              <label className="title">Módulo Marca</label>
              <label className="description">Logótipo, cores e simuladores personalizados</label>
              <label className="extra">Isento se atingires 750€ de produção nova cobrada no mês</label>
            </div>

            <div className="pricing">
              <label style={subscriptionPlan.branding ? {} : { color: '#99a1af' }} className="price">+{convertPricetoFormat(getModulePrice("BRANDING"), false, "", 2, 2)} €/mês</label>
              <div style={subscriptionPlan.branding ? { backgroundColor: 'var(--secondary-color)' } : { border: '1px solid #ebe6e7' }} className="icon-background">
                <CheckIcon
                  fill="white"
                  size="20"
                />
              </div>
            </div>
          </div>

          {/* MudeyAI Module */}
          <div onClick={() => handleModuleClick('MUDEYAI')} className="mudeyai-module">
            <div style={{ backgroundColor: 'var(--third-color)'}} className="img-wrapper">
              <img src={mudeyAiIcon} />
            </div>

            <div className="info">
              <label className="title">Módulo Mudey AI</label>
              <label className="description">Acesso a tecnologia de IA disponibilizada pela Mudey</label>
              <label className="extra">Incluído gratuitamente na tua conta MudeyPRO</label>
            </div>

            <div className="pricing">
              <label className="price">{getModulePriceLabel("MUDEYAI")}</label>
              <div style={{ backgroundColor: 'var(--third-color)' }} className="icon-background">
                <CheckIcon
                  fill="white"
                  size="20"
                />
              </div>
            </div>
          </div>

          {/* GuroAI Module */}
          <div onClick={() => handleModuleClick('GUROAI')} className="guro-module">
            <div style={{ backgroundColor: 'var(--third-color)'}} className="img-wrapper">
              <img src={guroIcon} />
            </div>

            <div className="info">
              <div className="title-row">
                <label className="title">Módulo GURO AI</label>
                <label style={{ backgroundColor: '#FBF0E2' }} className="badge">Novidade</label>
              </div>
              <label className="description">Conhecimento especializado potenciado por AI</label>
              <label className="extra">Incluído gratuitamente na tua conta MudeyPRO</label>
            </div>

            <div className="pricing">
              <label className="price">{getModulePriceLabel("GUROAI")}</label>
              <div style={{ backgroundColor: 'var(--third-color)' }} className="icon-background">
                <CheckIcon
                  fill="white"
                  size="20"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="warning">
          <InfoIcon
            fill="var(--primary-color)"
            size="20"
          />
          <label>As alterações ficam disponíveis de imediato, e serão consideradas no próximo débito direto.</label>
        </div>

        <div className="final">
          <label className="total">{convertPricetoFormat(getMonthlyValue(), false, "", 2, 2)} €/mês</label>
          <label className="description">Faturado mensalmente • Acresce IVA à taxa legal</label>
          <Buttons
            text="Activar subscrição"
            className="activate-subscription-button"
            callParentFunction={handleActivateSubscription}
            icon={DashboardArrowIcon}
            noMobile
          />
        </div>
      </div>
    </section>
  );
}
